import { jsPDF } from "jspdf";
import { withImageVersion } from "./imageVersion";
import { saveOrShareBlob } from "./share";

/**
 * Fetch a URL and return it as a base64 data URI (so jsPDF can embed it).
 * Works for both same-origin static files and CORS-enabled external CDNs.
 */
async function urlToDataUrl(url) {
    const resp = await fetch(url, { credentials: "omit" });
    const blob = await resp.blob();
    return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

function formatDate(iso) {
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            year: "numeric", month: "long", day: "numeric",
        });
    } catch {
        return iso;
    }
}

/**
 * Build a one-page A4 portrait certificate for the chosen angel and trigger a
 * browser download. Pure client-side via jsPDF.
 *
 * @param {object} args
 * @param {object} args.angel
 * @param {string} args.memberName
 * @param {string} args.selectionDate
 * @param {"designed"|"sketch"} [args.variant="designed"]
 */
export async function downloadCertificate({ angel, selectionDate, memberName, variant = "designed", firstMember = false }) {
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const W = doc.internal.pageSize.getWidth();   // 210
    const H = doc.internal.pageSize.getHeight();  // 297

    // Background — warm black
    doc.setFillColor(12, 11, 10);
    doc.rect(0, 0, W, H, "F");

    // Hairline brass border
    doc.setDrawColor(207, 160, 82);
    doc.setLineWidth(0.4);
    doc.rect(10, 10, W - 20, H - 20);
    doc.setLineWidth(0.15);
    doc.rect(13, 13, W - 26, H - 26);

    // Header
    doc.setTextColor(207, 160, 82);
    doc.setFont("times", "italic");
    doc.setFontSize(13);
    doc.text("Angel Squad", W / 2, 28, { align: "center" });

    doc.setTextColor(232, 228, 219);
    doc.setFont("times", "normal");
    doc.setFontSize(26);
    doc.text("Certificate of Selection", W / 2, 42, { align: "center" });

    doc.setDrawColor(207, 160, 82);
    doc.setLineWidth(0.3);
    doc.line(W / 2 - 30, 47, W / 2 + 30, 47);

    // Artwork — angel cards are 5:4 landscape (900x720) so we draw into a
    // matching 5:4 box to avoid the squish/stretch that comes from forcing
    // them into a portrait frame.
    try {
        const useSketch = variant === "sketch" && angel.placeholderImageUrl;
        const sourceUrl = useSketch ? angel.placeholderImageUrl : angel.mainImageUrl;
        const sourceVersion = useSketch ? angel.placeholderImageVersion : angel.imageVersion;
        const src = withImageVersion(sourceUrl, sourceVersion);
        const dataUrl = await urlToDataUrl(src);
        const artW = 140;          // mm — wider so the landscape art breathes
        const artH = artW * (4 / 5); // 5:4 landscape → 112mm tall
        const artX = (W - artW) / 2;
        const artY = 62;
        // soft brass frame behind the art
        doc.setDrawColor(207, 160, 82);
        doc.setLineWidth(0.3);
        doc.rect(artX - 2, artY - 2, artW + 4, artH + 4);
        doc.addImage(dataUrl, "JPEG", artX, artY, artW, artH, undefined, "FAST");
    } catch (e) {
        // Skip image silently — still produce a usable certificate
        console.warn("Certificate artwork could not be embedded", e);
    }

    // Big numeral
    doc.setFont("times", "normal");
    doc.setFontSize(72);
    doc.setTextColor(207, 160, 82);
    doc.text(angel.number, W / 2, 215, { align: "center" });

    doc.setFontSize(10);
    doc.setTextColor(154, 149, 138);
    doc.text("ANGEL  NUMBER", W / 2, 222, { align: "center" });

    // Permanent honour — this member was the first to choose this Angel.
    if (firstMember) {
        doc.setFont("times", "italic");
        doc.setFontSize(10);
        doc.setTextColor(207, 160, 82);
        doc.text("\u2014  first to choose  \u2014", W / 2, 229, { align: "center" });
    }

    // Member name
    doc.setFont("times", "italic");
    doc.setFontSize(20);
    doc.setTextColor(232, 228, 219);
    doc.text(`for ${memberName}`, W / 2, 240, { align: "center" });

    // Blessing
    doc.setFont("times", "italic");
    doc.setFontSize(11);
    doc.setTextColor(154, 149, 138);
    const blessing = "may this number walk with you. may the squad lift you. may your assignment unfold in its own quiet time.";
    const lines = doc.splitTextToSize(blessing, W - 60);
    doc.text(lines, W / 2, 252, { align: "center" });

    // Footer — date
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(110, 106, 98);
    doc.text(`SEALED  ${formatDate(selectionDate).toUpperCase()}`, W / 2, H - 18, { align: "center" });
    doc.text("myangelsquad.com", W / 2, H - 14, { align: "center" });

    const safeName = (memberName || "member").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
    const variantTag = variant === "sketch" ? "-sketch" : "";
    const filename = `angel-${angel.number}${variantTag}-${safeName}.pdf`;
    const blob = doc.output("blob");
    return await saveOrShareBlob({
        blob,
        filename,
        mimeType: "application/pdf",
        title: `Angel ${angel.number} certificate`,
    });
}
